import { createBrowserRouter } from 'react-router-dom'

import { AuthLayout } from '@/app/layouts/AuthLayout'
import { MainLayout } from '@/app/layouts/MainLayout'
import { LocaleScope } from '@/app/LocaleScope'
import { RouteErrorPage } from '@/app/RouteErrorPage'
import { SuspenseShell } from '@/app/SuspenseShell'
import { adminRoutes } from '@/features/admin/routes/adminRoutes'
import { ProtectedRoute } from '@/features/auth/guards/ProtectedRoute'
import { RoleRoute } from '@/features/auth/guards/RoleRoute'
import { RequireComms } from '@/features/communications/guards/RequireComms'
import { lazyPage } from '@/shared/lib/chunkLoad'

const LandingPage = lazyPage(() => import('@/features/landing/pages/LandingPage'))
const SessionsListPage = lazyPage(() => import('@/features/sessions/pages/SessionsListPage'))
const SessionDetailPage = lazyPage(() => import('@/features/sessions/pages/SessionDetailPage'))
const CreateSessionPage = lazyPage(() => import('@/features/sessions/pages/CreateSessionPage'))
const MySessionsPage = lazyPage(() => import('@/features/sessions/pages/MySessionsPage'))
const NotFoundPage = lazyPage(() => import('@/features/sessions/pages/NotFoundPage'))
const LoginPage = lazyPage(() => import('@/features/auth/pages/LoginPage'))
const RegisterPage = lazyPage(() => import('@/features/auth/pages/RegisterPage'))
const ForgotPasswordPage = lazyPage(() => import('@/features/auth/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazyPage(() => import('@/features/auth/pages/ResetPasswordPage'))
const VerifyEmailPage = lazyPage(() => import('@/features/auth/pages/VerifyEmailPage'))
const ProfilePage = lazyPage(() => import('@/features/profile/pages/ProfilePage'))
const MessagesPage = lazyPage(() => import('@/features/messages/pages/MessagesPage'))
const NotificationsPage = lazyPage(() => import('@/features/notifications/pages/NotificationsPage'))
const CommunicationsListPage = lazyPage(
  () => import('@/features/communications/pages/CommunicationsListPage'),
)
const CommunicationEditorPage = lazyPage(
  () => import('@/features/communications/pages/CommunicationEditorPage'),
)
const ScheduleEditorPage = lazyPage(
  () => import('@/features/communications/pages/ScheduleEditorPage'),
)
const HelpHubPage = lazyPage(() => import('@/features/help/pages/HelpHubPage'))
const HelpHowItWorksPage = lazyPage(() => import('@/features/help/pages/HelpHowItWorksPage'))
const HelpReliabilityPage = lazyPage(() => import('@/features/help/pages/HelpReliabilityPage'))
const HelpCancellationPage = lazyPage(() => import('@/features/help/pages/HelpCancellationPage'))
const HelpRatingsPage = lazyPage(() => import('@/features/help/pages/HelpRatingsPage'))
const HelpWaitlistPage = lazyPage(() => import('@/features/help/pages/HelpWaitlistPage'))
const HelpStoresPage = lazyPage(() => import('@/features/help/pages/HelpStoresPage'))
const RatePage = lazyPage(() => import('@/features/ratings/pages/RatePage'))
const PublicUserPage = lazyPage(() => import('@/features/users/pages/PublicUserPage'))
const AboutPage = lazyPage(() => import('@/features/legal/pages/AboutPage'))
const TermsPage = lazyPage(() => import('@/features/legal/pages/TermsPage'))
const PrivacyPage = lazyPage(() => import('@/features/legal/pages/PrivacyPage'))
const ChildSafetyPage = lazyPage(() => import('@/features/legal/pages/ChildSafetyPage'))
const ContactPage = lazyPage(() => import('@/features/contact/pages/ContactPage'))
const UnsubscribePage = lazyPage(() => import('@/features/city-waitlist/pages/UnsubscribePage'))
const EmailUnsubscribePage = lazyPage(() => import('@/features/emails/pages/EmailUnsubscribePage'))
const GameCityLandingPage = lazyPage(
  () => import('@/features/seo-landing/pages/GameCityLandingPage'),
)
const CityHubPage = lazyPage(() => import('@/features/seo-landing/pages/CityHubPage'))
const StoreDirectoryPage = lazyPage(() => import('@/features/stores/pages/StoreDirectoryPage'))
const StorePublicPage = lazyPage(() => import('@/features/stores/pages/StorePublicPage'))
const StoreHomePage = lazyPage(() => import('@/features/stores/owner/StoreHomePage'))
const AgendaPage = lazyPage(() => import('@/features/stores/owner/AgendaPage'))
const StoreStatsPage = lazyPage(() => import('@/features/stores/owner/StoreStatsPage'))
const StoreEventsPage = lazyPage(() => import('@/features/stores/owner/StoreEventsPage'))
const StoreVipsPage = lazyPage(() => import('@/features/stores/owner/StoreVipsPage'))
const MyStorePage = lazyPage(() => import('@/features/stores/owner/MyStorePage'))
const StoreSettingsPage = lazyPage(() => import('@/features/stores/owner/StoreSettingsPage'))
const NewVenuePage = lazyPage(() => import('@/features/stores/owner/NewVenuePage'))
const VenueLayout = lazyPage(() => import('@/features/stores/owner/VenueLayout'))
const VenueFichaPage = lazyPage(() => import('@/features/stores/owner/VenueFichaPage'))
const VenueCampaignsPage = lazyPage(() => import('@/features/stores/owner/VenueCampaignsPage'))
const NewVenueCampaignPage = lazyPage(() => import('@/features/stores/owner/NewVenueCampaignPage'))
const EditVenueCampaignPage = lazyPage(
  () => import('@/features/stores/owner/EditVenueCampaignPage'),
)

export const router = createBrowserRouter([
  {
    element: <SuspenseShell />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <MainLayout />,
        children: [
          adminRoutes,
          { path: '/', element: <LandingPage /> },
          { path: '/sessions', element: <SessionsListPage /> },
          {
            path: '/sessions/new',
            element: (
              <ProtectedRoute>
                <CreateSessionPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/sessions/mine',
            element: (
              <ProtectedRoute>
                <MySessionsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/sessions/:id/rate',
            element: (
              <ProtectedRoute>
                <RatePage />
              </ProtectedRoute>
            ),
          },
          { path: '/sessions/:id', element: <SessionDetailPage /> },
          { path: '/users/:username', element: <PublicUserPage /> },
          {
            path: '/help',
            element: (
              <LocaleScope locale="es">
                <HelpHubPage />
              </LocaleScope>
            ),
          },
          {
            path: '/help/como-funciona',
            element: (
              <LocaleScope locale="es">
                <HelpHowItWorksPage />
              </LocaleScope>
            ),
          },
          {
            path: '/help/fiabilidad',
            element: (
              <LocaleScope locale="es">
                <HelpReliabilityPage />
              </LocaleScope>
            ),
          },
          {
            path: '/help/cancelar-partida',
            element: (
              <LocaleScope locale="es">
                <HelpCancellationPage />
              </LocaleScope>
            ),
          },
          {
            path: '/help/valoraciones',
            element: (
              <LocaleScope locale="es">
                <HelpRatingsPage />
              </LocaleScope>
            ),
          },
          {
            path: '/help/lista-de-espera',
            element: (
              <LocaleScope locale="es">
                <HelpWaitlistPage />
              </LocaleScope>
            ),
          },
          {
            path: '/help/tiendas',
            element: (
              <LocaleScope locale="es">
                <HelpStoresPage />
              </LocaleScope>
            ),
          },
          {
            path: '/about',
            element: (
              <LocaleScope locale="es">
                <AboutPage />
              </LocaleScope>
            ),
          },
          {
            path: '/terms',
            element: (
              <LocaleScope locale="es">
                <TermsPage />
              </LocaleScope>
            ),
          },
          {
            path: '/privacy',
            element: (
              <LocaleScope locale="es">
                <PrivacyPage />
              </LocaleScope>
            ),
          },
          {
            path: '/child-safety',
            element: (
              <LocaleScope locale="es">
                <ChildSafetyPage />
              </LocaleScope>
            ),
          },
          // EN mirrors of the 9 SSG static routes. See routePaths.ts for the
          // single source of truth for slug translations. New SSG routes
          // MUST be added in both blocks + routePaths.ts + sitemap.
          {
            path: '/en/help',
            element: (
              <LocaleScope locale="en">
                <HelpHubPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/help/how-it-works',
            element: (
              <LocaleScope locale="en">
                <HelpHowItWorksPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/help/reliability',
            element: (
              <LocaleScope locale="en">
                <HelpReliabilityPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/help/cancel-game',
            element: (
              <LocaleScope locale="en">
                <HelpCancellationPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/help/ratings',
            element: (
              <LocaleScope locale="en">
                <HelpRatingsPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/help/waitlist',
            element: (
              <LocaleScope locale="en">
                <HelpWaitlistPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/help/stores',
            element: (
              <LocaleScope locale="en">
                <HelpStoresPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/about',
            element: (
              <LocaleScope locale="en">
                <AboutPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/terms',
            element: (
              <LocaleScope locale="en">
                <TermsPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/privacy',
            element: (
              <LocaleScope locale="en">
                <PrivacyPage />
              </LocaleScope>
            ),
          },
          {
            path: '/en/child-safety',
            element: (
              <LocaleScope locale="en">
                <ChildSafetyPage />
              </LocaleScope>
            ),
          },
          { path: '/contact', element: <ContactPage /> },
          { path: '/waitlist/baja', element: <UnsubscribePage /> },
          { path: '/emails/baja', element: <EmailUnsubscribePage /> },
          {
            path: '/profile',
            element: (
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/messages',
            element: (
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/messages/:sessionId',
            element: (
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            ),
          },
          {
            // Chat de un grupo de ganas de jugar, entrando por mis ganas (spec 2026-10-12).
            path: '/messages/group/:intentId',
            element: (
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/notifications',
            element: (
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            ),
          },
          // Comunicaciones (spec 2026-10-09): permiso de comunicaciones o admin.
          {
            path: '/comunicaciones',
            element: (
              <ProtectedRoute>
                <RequireComms>
                  <CommunicationsListPage />
                </RequireComms>
              </ProtectedRoute>
            ),
          },
          {
            path: '/comunicaciones/nuevo',
            element: (
              <ProtectedRoute>
                <RequireComms>
                  <CommunicationEditorPage />
                </RequireComms>
              </ProtectedRoute>
            ),
          },
          // Envíos semanales (spec 2026-11-03-comunicaciones-recurrentes).
          {
            path: '/comunicaciones/semanales/nuevo',
            element: (
              <ProtectedRoute>
                <RequireComms>
                  <ScheduleEditorPage />
                </RequireComms>
              </ProtectedRoute>
            ),
          },
          {
            path: '/comunicaciones/semanales/:id',
            element: (
              <ProtectedRoute>
                <RequireComms>
                  <ScheduleEditorPage />
                </RequireComms>
              </ProtectedRoute>
            ),
          },
          {
            path: '/comunicaciones/:id',
            element: (
              <ProtectedRoute>
                <RequireComms>
                  <CommunicationEditorPage />
                </RequireComms>
              </ProtectedRoute>
            ),
          },
          {
            path: '/tienda',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <StoreHomePage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/agenda',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <AgendaPage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/estadisticas',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <StoreStatsPage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/eventos',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <StoreEventsPage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/vips',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <StoreVipsPage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/editar',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <MyStorePage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/ajustes',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <StoreSettingsPage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/nuevo-local',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <NewVenuePage />
              </RoleRoute>
            ),
          },
          {
            path: '/tienda/locales/:id',
            element: (
              <RoleRoute roles={['TIENDA']}>
                <VenueLayout />
              </RoleRoute>
            ),
            children: [
              { index: true, element: <VenueFichaPage /> },
              { path: 'carteleras', element: <VenueCampaignsPage /> },
              { path: 'carteleras/nueva', element: <NewVenueCampaignPage /> },
              { path: 'carteleras/:cid', element: <EditVenueCampaignPage /> },
            ],
          },
          // Landings SEO programáticas (spec 2026-09-26-seo-juego-ciudad). Solo
          // español (RNF3). Distinto nº de segmentos → no colisionan entre sí.
          {
            path: '/partidas/:citySlug',
            element: (
              <LocaleScope locale="es">
                <CityHubPage />
              </LocaleScope>
            ),
          },
          {
            path: '/partidas/:gameSlug/:citySlug',
            element: (
              <LocaleScope locale="es">
                <GameCityLandingPage />
              </LocaleScope>
            ),
          },
          { path: '/locales', element: <StoreDirectoryPage /> },
          { path: '/locales/:slug', element: <StorePublicPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
          { path: '/verify-email', element: <VerifyEmailPage /> },
        ],
      },
    ],
  },
])
